import {
  Alert,
  Box,
  Button,
  ColumnLayout,
  Container,
  Form,
  FormField,
  Header,
  Input,
  Select,
  SpaceBetween,
  Textarea,
} from '@cloudscape-design/components';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useState } from 'react';

import { createVoiceSalesCampaign, type VoiceSalesCampaignCreateBody } from '../../common/api/voiceSales';
import { DashboardViewLayout } from '../../components/DashboardViewLayout';

const SOURCE_OPTIONS = [
  {
    label: 'Dallas contact discovery',
    value: 'dallas_zip_blast',
    description: 'Build an audience from approved Dallas-area contact discovery.',
  },
];

type FormState = {
  name: string;
  region: string;
  source: string;
  dailyCap: string;
  notes: string;
};

const INITIAL_FORM: FormState = {
  name: '',
  region: 'Dallas',
  source: SOURCE_OPTIONS[0].value,
  dailyCap: '10',
  notes: '',
};

export default function VoiceSalesCampaignCreatePage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [form, setForm] = useState<FormState>(INITIAL_FORM);
  const [submitted, setSubmitted] = useState(false);
  const dailyCap = Number(form.dailyCap);
  const nameError = submitted && !form.name.trim() ? 'Campaign name is required.' : undefined;
  const capError = submitted && (!Number.isInteger(dailyCap) || dailyCap < 1 || dailyCap > 10)
    ? 'Daily cap must be between 1 and 10.'
    : undefined;

  const createMutation = useMutation({
    mutationFn: (body: VoiceSalesCampaignCreateBody) => createVoiceSalesCampaign(body),
    onSuccess: async (campaign) => {
      await queryClient.invalidateQueries({ queryKey: ['voice-sales-campaigns'] });
      await navigate({
        to: '/dashboard/voice-sales/campaigns/$campaignUuid',
        params: { campaignUuid: campaign.uuid },
      });
    },
  });

  const submit = () => {
    setSubmitted(true);
    if (nameError || capError || !form.name.trim() || !Number.isInteger(dailyCap) || dailyCap < 1 || dailyCap > 10) return;
    createMutation.mutate({
      name: form.name.trim(),
      source_region: form.region.trim() || 'Dallas',
      source_kind: form.source,
      daily_cap: dailyCap,
      dry_run: true,
      status: 'draft',
      notes: form.notes.trim() || null,
    });
  };

  return (
    <DashboardViewLayout
      breadcrumbs={[
        { text: 'Voice Sales', href: '/dashboard/voice-sales' },
        { text: 'Campaigns', href: '/dashboard/voice-sales/campaigns' },
        { text: 'Create campaign', href: '/dashboard/voice-sales/campaigns/new' },
      ]}
      header={
        <Header
          variant="h1"
          description="Build a preview campaign through audience, compliance, scheduling, and review."
          actions={
            <SpaceBetween direction="horizontal" size="xs">
              <Button onClick={() => navigate({ to: '/dashboard/voice-sales/campaigns' })}>Cancel</Button>
              <Button variant="primary" loading={createMutation.isPending} onClick={submit}>
                Create draft campaign
              </Button>
            </SpaceBetween>
          }
        >
          Create campaign
        </Header>
      }
    >
      <Form>
        <SpaceBetween size="l">
          <Container header={<Header variant="h2">1. Audience</Header>}>
            <ColumnLayout columns={2}>
              <SpaceBetween size="m">
                <FormField
                  label="Campaign name"
                  description="Use a name your team will recognize in call review."
                  errorText={nameError}
                >
                  <Input
                    value={form.name}
                    placeholder="Dallas restaurant outreach"
                    invalid={Boolean(nameError)}
                    onChange={({ detail }) => setForm((current) => ({ ...current, name: detail.value }))}
                  />
                </FormField>
                <FormField label="Source region">
                  <Input value={form.region} onChange={({ detail }) => setForm((current) => ({ ...current, region: detail.value }))} />
                </FormField>
              </SpaceBetween>
              <SpaceBetween size="m">
                <FormField label="Audience source" description="Choose where approved contacts should come from.">
                  <SpaceBetween size="xxs">
                    <Select
                      selectedOption={SOURCE_OPTIONS.find((option) => option.value === form.source) ?? SOURCE_OPTIONS[0]}
                      options={SOURCE_OPTIONS}
                      onChange={({ detail }) => setForm((current) => ({ ...current, source: detail.selectedOption.value ?? SOURCE_OPTIONS[0].value }))}
                    />
                    <Box variant="small" color="text-body-secondary">
                      Build an audience from approved Dallas-area contact discovery.
                    </Box>
                  </SpaceBetween>
                </FormField>
                <FormField label="Notes (optional)">
                  <Textarea value={form.notes} rows={4} placeholder="Goals or context for the calling team." onChange={({ detail }) => setForm((current) => ({ ...current, notes: detail.value }))} />
                </FormField>
              </SpaceBetween>
            </ColumnLayout>
          </Container>

          <Container header={<Header variant="h2">2. Compliance</Header>}>
            <Alert type="info" header="Preview mode is required">
              New campaigns cannot place customer calls. Every attempt routes only to Max at 940-326-5175 until launch approval is complete.
            </Alert>
          </Container>

          <Container header={<Header variant="h2">3. Scheduling</Header>}>
            <FormField
              label="Daily call limit"
              description="Choose 1–10 calls. The service will not schedule more than 10 per day."
              errorText={capError}
            >
              <Input
                value={form.dailyCap}
                inputMode="numeric"
                invalid={Boolean(capError)}
                onChange={({ detail }) => setForm((current) => ({ ...current, dailyCap: detail.value }))}
              />
            </FormField>
          </Container>

          <Container header={<Header variant="h2">4. Review</Header>}>
            <ColumnLayout columns={4} variant="text-grid">
              <div><Box variant="awsui-key-label">Audience</Box><Box variant="p">Dallas contact discovery</Box></div>
              <div><Box variant="awsui-key-label">Region</Box><Box variant="p">{form.region || 'Dallas'}</Box></div>
              <div><Box variant="awsui-key-label">Daily limit</Box><Box variant="p">{form.dailyCap || '10'} per day</Box></div>
              <div><Box variant="awsui-key-label">Calling mode</Box><Box variant="p">Preview only</Box></div>
            </ColumnLayout>
          </Container>

          {createMutation.isError ? (
            <Alert type="error" header="Campaign could not be created">
              Review the campaign details and Retry.
            </Alert>
          ) : null}
        </SpaceBetween>
      </Form>
    </DashboardViewLayout>
  );
}
